import { mapActive } from "../utils/map-utils";
import { RootStore } from "../../shared/stores/RootStore";
import {
  Feature,
  GeoJsonProperties,
  LineString,
  Polygon,
  Position,
} from "geojson";
import { BBoxSelection } from "./BBoxSelection";
import { CustomSource } from "../models/CustomSource";
import {
  buildDefaultCategories,
  CompositeCategory,
} from "../../project/models/CompositeCategory";
import { categorizeCompositeFeature } from "../utils/query/categorizeCompositeFeature";
import { findAdjacentFeatures } from "../utils/query/findAdjacentFeatures";
import { relevantWayLayers } from "../utils/mapbox";
import { buildEmptyCreateHlaWay, CreateHlaWay } from "../../ra/models/HlaWay";
import * as turf from "@turf/turf";
import {
  delay,
  FacilityType,
  FacilityWidth,
  FlowDirectionType,
  HeavyVehicleFlowType,
} from "@keita/lanepatrol-sdk";
import {
  buildCreateOrUpdateHlaLane,
  PresenceDistance,
} from "../../ra/models/HlaLane";
import { HlaWayType } from "../../ra/models/HlaWay";
import { GeoJSONFeature } from "mapbox-gl";
import { applyAdjacentFeaturesToLane } from "../utils/query/applyAdjacentFeaturesToLane";
import { toast, toaster } from "../../shared/views/CustomToaster";

type BBox = [mapboxgl.Point, mapboxgl.Point];
type Side = "left" | "right";

export class QueryInfrastructureMapSource {
  map: mapboxgl.Map;
  rootStore: RootStore;
  enabled = false;
  bboxSelection?: BBoxSelection;
  bbox?: BBox;
  featuresInSelection: GeoJSONFeature[] = [];
  activeLayers: string[] = [];

  rightHandTraffic = true;
  frontSide: Side = "right";
  backSide: Side = "left";

  constructor(map: mapboxgl.Map, rootStore: RootStore) {
    this.map = map;
    this.rootStore = rootStore;
  }

  disable() {
    if (this.enabled) {
      this.bboxSelection?.disable();
      this.bboxSelection = undefined;
      this.bbox = undefined;
      this.featuresInSelection = [];
      this.clearActiveLayers();
      this.map.removeLayer(fillLayerConfig.id);
      this.map.removeLayer(lineLayerConfig.id);
      this.map.removeSource(sourceName);

      this.map.removeLayer(parkingLayer.id);
      this.map.removeLayer(busStopLayer.id);

      this.enabled = false;
    }
  }

  applyProjectConfig() {
    const { projectStore } = this.rootStore;

    this.rightHandTraffic = projectStore.rightHandTraffic;
    this.frontSide = projectStore.rightHandTraffic ? "right" : "left";
    this.backSide = projectStore.rightHandTraffic ? "left" : "right";
  }

  enable() {
    const { mapQueryStore } = this.rootStore;

    this.applyProjectConfig();

    if (mapActive(this.map) && !this.map.getSource(sourceName)) {
      this.map.addSource(sourceName, sourceConfig);
      this.map.addLayer(fillLayerConfig);
      this.map.addLayer(lineLayerConfig);

      this.map.addLayer(parkingLayer);
      this.map.addLayer(busStopLayer);
    }

    this.bboxSelection = new BBoxSelection(this.map, {
      onDrag: (bbox) => {
        this.setBBox(bbox);
        this.featuresInSelection = this.queryLayers(bbox);

        const categories = this.extractCompositeCategories(
          this.featuresInSelection,
        );
        mapQueryStore.setCompositeCategories(categories);
      },
      onClick: (point) => {
        this.setSourceData(undefined);
      },
    });

    this.bboxSelection.enable();

    this.enabled = true;
  }

  private clearActiveLayers() {
    this.activeLayers.forEach((layerName) => {
      this.map.removeLayer(layerName);
    });
    this.activeLayers = [];
  }

  highlightCategories(categories: CompositeCategory[]) {
    this.clearActiveLayers();

    categories.forEach((category) => {
      if (category.enabled) {
        category.items.forEach((item) => {
          const layerId = `highlight-${item.class}-${item.type}`;
          this.activeLayers.push(layerId);
          this.map.addLayer(
            buildHighlightLayerSpec(layerId, category.color, [
              "all",
              ["==", "class", item.class],
              ["==", "type", item.type],
            ]),
          );
        });
      }

      if (category.name === HlaWayType.Cycleway && category.enabled) {
        const layerId = `highlight-attached-cycleways`;
        this.activeLayers.push(layerId);
        this.map.addLayer(
          buildHighlightLayerSpec(layerId, category.color, [
            "all",
            ["has", "bike_lane"],
            ["!=", "bike_lane", "no"],
          ]),
        );
      }
    });
  }

  async importSelectedCategories() {
    if (!this.bbox) return;

    const { mapQueryStore, hlaStore, campaignStore } = this.rootStore;

    const totalFeatures = this.featuresInSelection.length;
    var featureCounter = 0;
    var wayCounter = 0;

    console.log("Starting import of bbox", this.bbox);

    const toastId = toast(
      "loading",
      `Querying your area selection. Please wait...`,
    );

    const campaign = campaignStore.selected!;

    const enabledCategories = mapQueryStore.compositeCategories
      .filter((cc) => cc.enabled)
      .map((cc) => cc.name);

    const bboxPolygon = this.pixelBBoxToLonLat(this.bbox);

    for (const feature of this.featuresInSelection) {
      const featureId = feature.id as number;
      const linesInsideBBox: Position[][] = [];

      // Roads may come in groups and some of the lines in each group may be outside the bbox
      if (feature.geometry.type === "LineString") {
        linesInsideBBox.push(feature.geometry.coordinates);
      } else if (feature.geometry.type === "MultiLineString") {
        for (const coordinates of feature.geometry.coordinates) {
          const lineString = turf.lineString(coordinates);

          // We ignore the lines that are outside the bbox
          if (turf.booleanIntersects(lineString, bboxPolygon)) {
            linesInsideBBox.push(coordinates);
          }
        }
      }

      for (const line of linesInsideBBox) {
        const way = await this.analyzeFeature(
          featureId,
          line,
          feature.properties,
          enabledCategories,
        );

        if (way) {
          console.log("Resulting way", way);

          way.campaignId = campaign.id;
          // TODO comment
          // this.addRequestToMap(way);
          // TODO uncomment
          await hlaStore.create(way);
          wayCounter++;
        }
      }

      featureCounter++;

      toaster.update(toastId, {
        title: `Processing features (${featureCounter} of ${totalFeatures})`,
      });
    }

    toaster.update(toastId, {
      type: "success",
      title: `Done! ${wayCounter} ways imported`,
    });

    hlaStore.updateMapSource();
    mapQueryStore.deactivate();
  }

  pixelBBoxToLonLat(bbox: BBox): Feature<Polygon> {
    const { x: minX, y: minY } = bbox[0];
    const { x: maxX, y: maxY } = bbox[1];

    const sw = this.map.unproject([minX, maxY]);
    const ne = this.map.unproject([maxX, minY]);

    return turf.bboxPolygon([sw.lng, sw.lat, ne.lng, ne.lat]);
  }

  async analyzeFeature(
    featureId: number,
    coordinates: Position[],
    properties: GeoJsonProperties,
    enabledCategories: HlaWayType[],
  ) {
    const includeCycleways = enabledCategories.includes(HlaWayType.Cycleway);

    const props = properties!;
    const type = props.type ?? "";
    const className = props.class ?? "";

    var categoryName = categorizeCompositeFeature(className, type);

    const roadWithCyclingInfrastructure =
      !!props.bike_lane && props.bike_lane !== "no";
    const shouldCreateCurrentLane = enabledCategories.includes(categoryName);

    if (
      !shouldCreateCurrentLane &&
      !(includeCycleways && roadWithCyclingInfrastructure)
    ) {
      console.log("Ignoring", categoryName);
      return;
    }

    const lineString: LineString = {
      type: "LineString",
      coordinates,
    };

    console.log("Waiting and moving...");
    const featureCenter = turf.centroid(lineString);
    this.map.jumpTo({
      center: featureCenter.geometry.coordinates as mapboxgl.LngLatLike,
      zoom: 16,
    });
    await delay(200);

    const way = buildEmptyCreateHlaWay();

    console.log("Importing:", categoryName, props);

    way.coordinates = coordinates;
    way.lengthM = turf.length(
      {
        type: "Feature",
        geometry: lineString,
        properties: null,
      },
      {
        units: "meters",
      },
    );

    way.type = categoryName;

    // Name
    if (props.name) {
      way.name = props.name;
    }

    way.frontFacingLane = buildCreateOrUpdateHlaLane(true);

    // Flow direction
    if (props.oneway) {
      way.frontFacingLane.flowDirection =
        props.oneway === "true"
          ? FlowDirectionType.OneWay
          : FlowDirectionType.TwoWay;
    }

    // Facility type and width
    if (categoryName === HlaWayType.Cycleway) {
      way.frontFacingLane.facilityType = FacilityType.OffRoadBicyclePath;
    } else if (
      categoryName === HlaWayType.Service ||
      categoryName === HlaWayType.Motorway ||
      categoryName === HlaWayType.Primary ||
      categoryName === HlaWayType.Secondary ||
      categoryName === HlaWayType.Tertiary ||
      categoryName === HlaWayType.Unclassified
    ) {
      way.frontFacingLane.facilityType = FacilityType.MixedTrafficRoadLane;
      way.frontFacingLane.facilityWidthPerDirection = FacilityWidth.Wide;
    } else if (categoryName === HlaWayType.Sidewalk) {
      way.frontFacingLane.facilityType = FacilityType.Sidewalk;
      way.frontFacingLane.facilityWidthPerDirection = FacilityWidth.Narrow;
    } else if (
      categoryName === HlaWayType.Pedestrian ||
      categoryName === HlaWayType.Platform ||
      categoryName === HlaWayType.FootPath
    ) {
      way.frontFacingLane.facilityType = FacilityType.MultiUsePath;
      if (categoryName === HlaWayType.Pedestrian) {
        way.frontFacingLane.facilityWidthPerDirection = FacilityWidth.Wide;
      } else {
        way.frontFacingLane.facilityWidthPerDirection =
          FacilityWidth.VeryNarrow;
      }
    }

    if (roadWithCyclingInfrastructure && includeCycleways) {
      if (props.bike_lane === this.frontSide || props.bike_lane === "both") {
        way.frontFacingLane.facilityType = FacilityType.OnRoadBicycleLane;
        way.frontFacingLane.facilityWidthPerDirection = FacilityWidth.Narrow;
        way.frontFacingLane.flowDirection = FlowDirectionType.OneWay;

        if (this.rightHandTraffic) {
          way.frontFacingLane.adjacentFeaturesLeft.roadLane =
            PresenceDistance.From0to1m;
        } else {
          way.frontFacingLane.adjacentFeaturesRight.roadLane =
            PresenceDistance.From0to1m;
        }
      }

      if (props.bike_lane === this.backSide || props.bike_lane === "both") {
        way.backFacingLane = buildCreateOrUpdateHlaLane(false);
        way.backFacingLane.facilityType = FacilityType.OnRoadBicycleLane;
        way.backFacingLane.facilityWidthPerDirection = FacilityWidth.Narrow;
        way.backFacingLane.flowDirection = FlowDirectionType.OneWay;

        if (this.rightHandTraffic) {
          way.backFacingLane.adjacentFeaturesLeft.roadLane =
            PresenceDistance.From0to1m;
        } else {
          way.backFacingLane.adjacentFeaturesRight.roadLane =
            PresenceDistance.From0to1m;
        }
      }
    }

    const adjacentFeatures = findAdjacentFeatures(
      featureId,
      lineString,
      this.map,
    );

    console.log("adjacentFeatures", adjacentFeatures);

    if (adjacentFeatures.BusStop.left || adjacentFeatures.BusStop.right) {
      way.heavyVehicleFlow = HeavyVehicleFlowType.MediumToHigh;
    }

    if (way.frontFacingLane) {
      applyAdjacentFeaturesToLane(way.frontFacingLane, adjacentFeatures);
    }

    if (way.backFacingLane) {
      applyAdjacentFeaturesToLane(way.backFacingLane, adjacentFeatures);
    }

    return way;
  }

  // just for testing
  private addRequestToMap(way: CreateHlaWay) {
    const { hlaStore } = this.rootStore;
    const wayId = Math.random().toString();

    hlaStore.setWays(
      hlaStore.ways.concat([
        {
          ...way,
          id: wayId,
          frontFacingLane: way.frontFacingLane
            ? {
                ...way.frontFacingLane,
                hlaWayId: wayId,
                score: null,
              }
            : null,
          backFacingLane: way.backFacingLane
            ? {
                ...way.backFacingLane,
                hlaWayId: wayId,
                score: null,
              }
            : null,
          createdAt: "",
        },
      ]),
    );
  }

  private queryLayers(bbox: BBox) {
    const features = this.map.queryRenderedFeatures(bbox, {
      layers: relevantWayLayers,
    });

    console.log("All queried features:", features);

    return features;
  }

  private extractCompositeCategories(features: mapboxgl.GeoJSONFeature[]) {
    const totalFeaturesPerType: Record<string, number> = {};
    const classToTypes: Record<string, Set<string>> = {};
    var totalRoadsWithBikeLanes = 0;

    const types: Set<string> = new Set();
    features.forEach((feature) => {
      const className = feature.properties!.class;
      const type = feature.properties!.type;
      const hasBikeLane =
        !!feature.properties?.bike_lane &&
        feature.properties.bike_lane !== "no";
      classToTypes[className] = classToTypes[className] ?? new Set();
      classToTypes[className].add(type);
      types.add(type);

      totalFeaturesPerType[type] = (totalFeaturesPerType[type] ?? 0) + 1;

      if (hasBikeLane) {
        totalRoadsWithBikeLanes++;
      }
    });

    const allCategories = buildDefaultCategories();

    const categoriesByName = new Map<string, CompositeCategory>(
      allCategories.map((c) => [c.name, c]),
    );

    for (const [className, types] of Object.entries(classToTypes)) {
      for (const type of types) {
        var name = categorizeCompositeFeature(className, type);

        const totalFeatures = totalFeaturesPerType[type];

        const category = categoriesByName.get(name)!;
        category.totalFeatures += totalFeatures;
        category.items.push({ class: className, type: type });
      }
    }

    const cyclewayCategory = categoriesByName.get(HlaWayType.Cycleway)!;
    cyclewayCategory.totalFeatures += totalRoadsWithBikeLanes;

    return [...categoriesByName.values()];
  }

  private setBBox(bbox: BBox) {
    this.bbox = bbox;

    const { mapQueryStore } = this.rootStore;
    const [pointA, pointB] = bbox;

    const coordsA = this.map.unproject([pointA.x, pointA.y]);
    const coordsB = this.map.unproject([pointB.x, pointB.y]);

    const polygon = [
      [coordsA.lng, coordsA.lat],
      [coordsB.lng, coordsA.lat],
      [coordsB.lng, coordsB.lat],
      [coordsA.lng, coordsB.lat],
      [coordsA.lng, coordsA.lat],
    ];

    mapQueryStore.setPolygon(polygon); // TODO can this be removed?

    this.setSourceData(polygon);
  }

  setSourceData(polygon: number[][] | undefined) {
    if (mapActive(this.map)) {
      const source = this.map.getSource(sourceName) as mapboxgl.GeoJSONSource;
      source?.setData({
        type: "FeatureCollection",
        features: [
          {
            type: "Feature",
            geometry: {
              type: "Polygon",
              coordinates: [polygon as Position[]],
            },
            properties: {},
          },
        ],
      });
    }
  }
}

function buildHighlightLayerSpec(
  id: string,
  color: string,
  filter: mapboxgl.FilterSpecification,
): mapboxgl.LineLayerSpecification {
  return {
    id,
    filter,
    type: "line",
    source: "composite",
    layout: {
      "line-join": "round",
      "line-cap": "round",
    },
    paint: {
      "line-color": color,
      "line-width": 4,
      "line-opacity": 0.8,
    },
    "source-layer": "road",
  };
}

const sourceName = CustomSource.QueryInfrastructure;
const sourceConfig: mapboxgl.GeoJSONSourceSpecification = {
  type: "geojson",
  promoteId: "id",
  data: {
    type: "FeatureCollection",
    features: [],
  },
};

const fillLayerName = `${sourceName}-fill`;
const fillLayerConfig: mapboxgl.FillLayerSpecification = {
  type: "fill",
  paint: {
    "fill-opacity": 0.1,
    "fill-color": "#12d3c4",
  },
  source: sourceName,
  id: fillLayerName,
};

const lineLayerName = `${sourceName}-line`;
const lineLayerConfig: mapboxgl.LineLayerSpecification = {
  type: "line",
  paint: {
    "line-opacity": 0.5,
    "line-color": "#047067",
  },
  source: sourceName,
  id: lineLayerName,
};

const parkingLayer: mapboxgl.CircleLayerSpecification = {
  type: "circle",
  paint: {
    "circle-radius": 7,
    "circle-opacity": 0,
    "circle-color": "transparent",
  },
  id: "parking",
  source: "composite",
  "source-layer": "poi_label",
  filter: ["==", "type", "Parking"],
};

const busStopLayer: mapboxgl.CircleLayerSpecification = {
  type: "circle",
  paint: {
    "circle-radius": 7,
    "circle-opacity": 0,
    "circle-color": "transparent",
  },
  id: "bus-stop",
  source: "composite",
  "source-layer": "transit_stop_label",
};

// const testLayerName = `test`;
// const testLayerConfig: mapboxgl.LineLayerSpecification = {
//   type: "line",
//   layout: {
//     "line-join": "round",
//     "line-cap": "round",
//   },
//   paint: {
//     "line-color": "#b81fe7",
//     "line-width": 5,
//   },
//   id: testLayerName,
//   source: "composite",
//   "source-layer": "road",
//   filter: ["all", isLineString, ["==", "type", "residential"]],
// };
